import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, ShieldCheck, Save, SkipForward, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import AIProgressIndicator from "@/components/AIProgressIndicator";
import { WIZARD_STEPS, WIZARD_TOTAL } from "@/lib/wizard";
import {
    ListStepForm,
    ScalarStepForm,
    UploadsStepForm,
} from "@/components/wizard/StepForms";

function useQueryStep() {
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    const step = Number(search.get("step") || "1");
    return Math.min(Math.max(step, 1), WIZARD_TOTAL);
}

export default function OnboardingWizard() {
    const nav = useNavigate();
    const step = useQueryStep();
    const stepDef = WIZARD_STEPS.find((s) => s.n === step);

    const [state, setState] = useState(null);
    const [uploads, setUploads] = useState([]);
    const [profileExt, setProfileExt] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, u, e] = await Promise.all([
                api.get("/onboarding/state"),
                api.get("/me/uploads").catch(() => ({ data: [] })),
                api.get("/me/profile-ext").catch(() => ({ data: {} })),
            ]);
            setState(s.data);
            setUploads(u.data || []);
            setProfileExt(e.data || {});
        } catch (err) {
            toast.error(errMsg(err, "Failed to load onboarding"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const doctor = state?.doctor;
    const profile = state?.profile || {};
    const itemsByKind = state?.items_by_kind || {};
    const unlocked = state?.unlocked;
    const status = doctor?.profile_status;

    const goto = (n) => nav(`/doctor/onboarding?step=${n}`);

    const patchProfile = async (patch) => {
        setSaving(true);
        try {
            const { data } = await api.put("/onboarding/profile", patch);
            setState((s) => ({ ...s, profile: data }));
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const patchProfileExt = async (patch) => {
        const { data } = await api.put("/me/profile-ext", patch);
        setProfileExt(data);
        return data;
    };

    const complete = async (advance = true) => {
        try {
            await api.post(`/onboarding/step/${step}/complete`);
            if (advance && step < WIZARD_TOTAL) goto(step + 1);
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Could not mark complete"));
        }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/onboarding/submit");
            toast.success("Submitted — AI is preparing your profile.");
            nav("/doctor?ai_job=" + data.job_id);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d && typeof d === "object" && Array.isArray(d.errors)) {
                d.errors.forEach((m) => toast.error(m));
            } else {
                toast.error(typeof d === "string" ? d : "Submission failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const requiredForSubmit = useMemo(
        () => WIZARD_STEPS.filter((s) => s.required).map((s) => s.n),
        []
    );
    const completedSet = new Set(state?.completed_steps || []);
    const progressPct = Math.round((completedSet.size / (WIZARD_TOTAL - 1)) * 100);

    if (loading) {
        return (
            <DoctorShell doctor={null}>
                <div className="animate-fade-up" data-testid="wizard-loading-skeleton">
                    <div className="ddi-skeleton h-3 w-32 mb-4" />
                    <div className="ddi-skeleton h-9 w-2/3 mb-2" />
                    <div className="ddi-skeleton h-4 w-1/2 mb-6" />
                    <div className="flex gap-1.5 mb-8">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="ddi-skeleton h-8 w-8" />
                        ))}
                    </div>
                    <div className="ddi-card ddi-shadow-md p-6 lg:p-8 space-y-4">
                        <div className="ddi-skeleton h-4 w-24" />
                        <div className="ddi-skeleton h-11 w-full" />
                        <div className="ddi-skeleton h-4 w-24 mt-4" />
                        <div className="ddi-skeleton h-11 w-full" />
                        <div className="ddi-skeleton h-4 w-24 mt-4" />
                        <div className="ddi-skeleton h-11 w-full" />
                    </div>
                </div>
            </DoctorShell>
        );
    }
    if (!unlocked) {
        return (
            <DoctorShell doctor={doctor}>
                <div className="ddi-card p-8 max-w-2xl" data-testid="wizard-locked-panel">
                    <div className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">
                        Locked
                    </div>
                    <h1 className="mt-1 text-2xl font-bold text-[#123B5D]">
                        Complete your advance payment to start onboarding
                    </h1>
                    <p className="mt-2 text-sm text-[#17212B]/70">
                        The 20-step onboarding wizard unlocks the moment your
                        advance payment is confirmed by our gateway webhook.
                    </p>
                    <button
                        className="mt-6 ddi-teal-btn"
                        onClick={() => nav("/doctor/packages")}
                        data-testid="wizard-goto-packages"
                    >
                        Choose a package
                    </button>
                </div>
            </DoctorShell>
        );
    }
    // Guard: allow only in editable statuses
    const editable = ["draft", "onboarding", "changes_requested"].includes(status);
    if (!editable) {
        return (
            <DoctorShell doctor={doctor}>
                <div className="ddi-card p-8 max-w-2xl" data-testid="wizard-not-editable">
                    <h1 className="text-2xl font-bold text-[#123B5D]">
                        Your profile is currently {status?.replaceAll("_", " ")}
                    </h1>
                    <p className="mt-2 text-sm text-[#17212B]/70">
                        Onboarding editing is disabled while your profile is in
                        this state. Check the Final Review or Dashboard for the
                        next action.
                    </p>
                </div>
            </DoctorShell>
        );
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="ddi-eyebrow text-[#0F766E]">
                            Onboarding · Step {step} of {WIZARD_TOTAL}
                        </div>
                        <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                            {stepDef.title}
                            {stepDef.required ? (
                                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#DC2626]/80 align-middle font-semibold rounded-md bg-[#FEE2E2] px-1.5 py-0.5">
                                    Required
                                </span>
                            ) : (
                                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#17212B]/40 align-middle font-semibold rounded-md bg-[#F1F5F9] px-1.5 py-0.5">
                                    Optional
                                </span>
                            )}
                        </h1>
                        {stepDef.description ? (
                            <p className="mt-2 text-sm text-[#17212B]/70 max-w-2xl leading-relaxed">
                                {stepDef.description}
                            </p>
                        ) : null}
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-[#17212B]/60 font-medium">
                            {completedSet.size} / {WIZARD_TOTAL - 1} steps saved · {progressPct}%
                        </div>
                        <div className="w-64 h-2 rounded-full bg-[#123B5D]/10 mt-1.5 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#0F766E] to-[#14B8A6] transition-all duration-500 ease-out"
                                style={{ width: `${progressPct}%` }}
                                data-testid="wizard-progress-bar"
                            />
                        </div>
                    </div>
                </div>

                {/* Step nav dots */}
                <div className="mt-6 flex flex-wrap gap-1.5" data-testid="wizard-step-dots">
                    {WIZARD_STEPS.map((s) => {
                        const done = completedSet.has(s.n);
                        const active = s.n === step;
                        const req = s.required;
                        return (
                            <button
                                key={s.n}
                                onClick={() => goto(s.n)}
                                className={
                                    "h-8 min-w-8 px-2.5 text-[11px] font-semibold rounded-lg transition-all duration-150 " +
                                    (active
                                        ? "bg-[#123B5D] text-white shadow-md shadow-[#123B5D]/25 scale-105"
                                        : done
                                        ? "bg-[#0F766E]/10 text-[#0F766E] hover:bg-[#0F766E]/20"
                                        : req
                                        ? "bg-white border border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEE2E2]/40"
                                        : "bg-white border border-[#123B5D]/10 text-[#17212B]/60 hover:border-[#123B5D]/25")
                                }
                                title={s.title}
                                data-testid={`wizard-dot-${s.n}`}
                            >
                                {s.n}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-8 ddi-card ddi-shadow-md p-6 lg:p-8" data-testid={`wizard-step-body-${step}`}>
                    {stepDef.kind === "scalar" ? (
                        <ScalarStepForm
                            step={stepDef}
                            profile={profile}
                            onPatch={patchProfile}
                            profileExt={profileExt}
                            onPatchProfileExt={patchProfileExt}
                        />
                    ) : stepDef.kind === "list" ? (
                        <ListStepForm
                            step={stepDef}
                            items={itemsByKind[stepDef.listKind] || []}
                            refresh={load}
                        />
                    ) : stepDef.kind === "uploads" ? (
                        <UploadsStepForm
                            step={stepDef}
                            uploads={uploads}
                            profile={profile}
                            onPatch={patchProfile}
                            refresh={load}
                        />
                    ) : stepDef.kind === "review" ? (
                        <FinalReviewStep
                            state={state}
                            uploads={uploads}
                            onSubmit={submit}
                            submitting={submitting}
                        />
                    ) : null}
                </div>

                <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                    <button
                        className="ddi-outline-btn text-sm inline-flex items-center gap-1 disabled:opacity-50"
                        disabled={step === 1}
                        onClick={() => goto(step - 1)}
                        data-testid="wizard-prev"
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                    <div className="flex items-center gap-2 text-xs text-[#17212B]/60">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {saving ? "Saving…" : "Auto-saved"}
                    </div>
                    <div className="flex items-center gap-2">
                        {!stepDef.required && step < WIZARD_TOTAL ? (
                            <button
                                className="text-sm text-[#17212B]/70 hover:text-[#123B5D] inline-flex items-center gap-1"
                                onClick={() => goto(step + 1)}
                                data-testid="wizard-skip"
                            >
                                <SkipForward size={14} /> Upload later
                            </button>
                        ) : null}
                        {step < WIZARD_TOTAL ? (
                            <button
                                className="ddi-teal-btn text-sm inline-flex items-center gap-1"
                                onClick={() => complete(true)}
                                data-testid="wizard-save-continue"
                            >
                                Save & continue <ArrowRight size={14} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </DoctorShell>
    );
}

function FinalReviewStep({ state, uploads, onSubmit, submitting }) {
    const p = state?.profile || {};
    const items = state?.items_by_kind || {};
    const count = (k) => (items[k] || []).length;
    return (
        <div className="space-y-6" data-testid="wizard-final-review">
            <div className="flex items-start gap-3 ddi-card p-4 border-[#0F766E]/30 bg-[#0F766E]/5">
                <ShieldCheck size={20} className="text-[#0F766E] mt-0.5" />
                <div className="text-sm text-[#17212B]/80">
                    <div className="font-bold text-[#0F766E]">
                        Ready when you are
                    </div>
                    <div className="mt-1">
                        Once submitted, our AI will refine your profile using only what you provided. Missing sections will be flagged “Information Required” — never fabricated.
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    ["Personal Info", `Gender: ${p.gender || "—"} · Languages: ${(p.languages || []).join(", ") || "—"}`],
                    ["Professional", `${p.registration_council || "—"} · ${p.registration_year || "—"} · ${p.years_of_experience ?? "—"} yrs`],
                    ["Specialization", `${p.primary_specialization || "—"}${(p.sub_specializations || []).length ? " · " + (p.sub_specializations || []).join(", ") : ""}`],
                    ["Education", `${count("education")} entries`],
                    ["Experience", `${count("experience")} entries`],
                    ["Clinics", `${count("clinic")} clinic(s)`],
                    ["Hospital Affiliations", `${count("hospital_affiliation")} entries`],
                    ["Consultation", `${(p.consultation_hours || []).length} slots · Base fee ₹${p.base_fee_inr ?? "—"}`],
                    ["Achievements", `${count("achievement")} entries`],
                    ["Awards", `${count("award")} entries`],
                    ["Publications", `${count("publication")} entries`],
                    ["Memberships", `${count("membership")} entries`],
                    ["Social Media", `${count("social_link")} link(s)`],
                    ["Patient Info", (p.patient_info?.what_to_expect || p.patient_info?.what_to_bring) ? "Provided" : "—"],
                    ["Health Education", `${count("health_education")} entries`],
                    ["Media", `${uploads?.length || 0} uploaded`],
                    ["Biography notes", (p.biography_notes || "").length > 0 ? "Provided" : "Missing (required)"],
                    ["SEO hints", (p.seo_keywords || []).length ? "Provided" : "—"],
                ].map(([k, v]) => (
                    <div key={k} className="ddi-card p-4 bg-[#F5F8FA]">
                        <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50">
                            {k}
                        </div>
                        <div className="mt-1 text-sm font-medium text-[#123B5D]">
                            {v}
                        </div>
                    </div>
                ))}
            </div>
            <div className="pt-2">
                <button
                    className="ddi-teal-btn text-base inline-flex items-center gap-2 disabled:opacity-60"
                    onClick={onSubmit}
                    disabled={submitting}
                    data-testid="wizard-submit-ai"
                >
                    {submitting ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <Sparkles size={16} />
                    )}
                    Submit for AI Processing
                </button>
                {submitting && (
                    <div className="mt-4" data-testid="wizard-ai-progress">
                        <AIProgressIndicator
                            status="processing"
                            startedAt={Date.now()}
                            label="AI is refining your profile"
                            sub="This typically takes 30–90 seconds. Nothing publishes without your review."
                            expectedSeconds={60}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
